// Monotonic latch over a timeline-derived entry, surviving the reconnect
// replay window.
//
// The WS attach effect wipes the timeline and replays the full bus on every
// reconnect, so a derivation over `ws.timeline` goes null and then
// walks the entry's HISTORY oldest-first until the latest re-lands. Consumers
// that render from the derivation would flash empty and transiently regress.
// This hook resolves `derived` against the last entry it handed out:
//   - derived null            → keep the latched entry (the wipe window);
//   - derived newer-or-equal  → take it (normal flow);
//   - derived OLDER           → keep the latched entry (mid-replay regression),
// ordering by the bus-monotonic seq tail of the entry id (`${sessionId}#${seq}`).
//
// `key` scopes validity: when it changes (issue navigation, session switch,
// reset epoch), the latched entry is invisible — it can never leak across
// identities. Callers must put EVERYTHING that re-bases the seq space into the
// key (sessionId, and the hook owner's resetEpoch — `POST /sessions/:id/reset`
// keeps the sessionId but rebuilds the bus from seq 0).
//
// SELF-HEAL: seq monotonicity can still silently break (a failed rehydrate
// swallows the event-log read and reseeds the bus at 0; a crash loses the
// log writer's last debounced batch). In a genuine replay "derived loses to
// latched" holds only for the sub-second replay burst; if it PERSISTS, the seq
// authority restarted and the latch is the stale side — so after a grace
// window the hook adopts the derived entry instead of wedging forever on the
// pre-restart high-water mark.

import { useEffect, useReducer, useRef } from "react";

import { entrySeq } from "./timeline";

// Longer than any realistic replay burst (a capped 4000-event bus streams in
// well under a second on one socket), shorter than a user notices a frozen
// card. A genuinely slow replay that trips this merely regresses to the
// pre-latch behavior for a moment and self-corrects on the next entry.
const STALE_LATCH_ADOPT_MS = 4000;

export function useLatchedEntry<T extends { readonly id: string }>(
  derived: T | null,
  key: string,
): T | null {
  const latchRef = useRef<{ key: string; entry: T } | null>(null);
  const [, bump] = useReducer((n: number) => n + 1, 0);

  const latched =
    latchRef.current !== null && latchRef.current.key === key
      ? latchRef.current.entry
      : null;
  const resolved =
    derived === null
      ? latched
      : latched === null || entrySeq(derived.id) >= entrySeq(latched.id)
        ? derived
        : latched;

  useEffect(() => {
    if (resolved !== null) latchRef.current = { key, entry: resolved };
  }, [resolved, key]);

  // The self-heal timer. `stale` is true exactly while a non-null derived is
  // losing to the latch; it stays true across derived churn (each new
  // low-seq entry), so the timer is NOT re-armed per entry — it measures how
  // long the regression persists, which is the signal.
  const stale = derived !== null && resolved !== derived;
  useEffect(() => {
    if (!stale) return;
    const timer = setTimeout(() => {
      // Drop the latch entirely; the next render resolves to the live
      // derivation, whatever its seq space is now.
      latchRef.current = null;
      bump();
    }, STALE_LATCH_ADOPT_MS);
    return () => clearTimeout(timer);
  }, [stale]);

  return resolved;
}
